import React, { useState, useEffect } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import pluralize from 'pluralize';
import { useData, useStatus, isLoading, combineStatuses } from 'resift';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from 'styles/ThemeTyping';
import makePostFilterGetUserCount from 'fetches/makePostFilterGetUserCount';
import createSearchFilterQueryParams from 'fetches/rolesAndPermissions/createSearchFilterQueryParams';
import getHostName from 'helpers/getHostName';
import {
  RELATIVE_FILTERS,
  CONTENT_TYPE_FIELD,
  CONTENT_TYPE_COMPARISON,
  CONTENT_TYPE_VALUE,
  CONTENT_TYPE_FIELD_FOR_SAME_ATTR,
  SAME_ATTR_RELATIVE_FILTER,
} from 'pages/RolesAndPermissions/consts';
import * as Types from 'pages/RolesAndPermissions/typings';

import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import Popper from '@material-ui/core/Popper';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import FilterEditPopper from './FilterEditPopper';
import FilterGroup from './FilterGroup';
import Empty from './Empty';
import SelectInput from './valueInput/SelectInput';

interface Props {
  filterGroups: Array<Types.FilterGroup>;
  outerOperator: Types.Operator;
  shouldDisableAddFilter: boolean;
  everyoneSelected: boolean;
  role: Types.Role;
  pageType: 'members' | 'permissions';
  onSetOuterOperator: (operator: Types.Operator) => void;
  onSetFilterGroups: (groups: Array<any>) => void;
  onSetEveryoneSelected: (selected: boolean) => void;
  onUpdateFilteredUsers?: (
    groups?: Array<Types.FilterGroup>,
    outerOperator?: Types.Operator,
  ) => void;
}

function Filters({
  filterGroups,
  outerOperator,
  shouldDisableAddFilter,
  everyoneSelected,
  role,
  pageType,
  onSetFilterGroups,
  onSetOuterOperator,
  onSetEveryoneSelected,
  onUpdateFilteredUsers,
}: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const getUserCount = makePostFilterGetUserCount();
  const [filterIdInFocus, setFilterIdInFocus] = useState<string>('');
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(null);
  const [outerOperatorAnchorEl, setOuterOperatorAnchorEl] = useState<null | HTMLElement>(null);
  const disabled = pageType === 'members' ? !role.membersEditable : !role.permissionsEditable;
  const filteredUserCount = useData(getUserCount);
  const getUserCountStatus = useStatus(getUserCount);
  const base64Filter = useData(createSearchFilterQueryParams);
  const getBase64FilterStatus = useStatus(createSearchFilterQueryParams);
  const clientSubdomain = useSelector((state: any) => state.client.subdomain);
  const hostName = getHostName();
  const combinedStatus = combineStatuses(getUserCountStatus, getBase64FilterStatus);
  const makeDirectoryUrl = (path: string) => `https://${clientSubdomain}.${hostName}.com${path}`;

  useEffect(() => {
    if (!onUpdateFilteredUsers) {
      return;
    }

    if (!everyoneSelected && filterGroups.length === 0) {
      return;
    }

    onUpdateFilteredUsers(filterGroups, outerOperator);
  }, []);

  const handleChangeOuterOperator = (newOperator: Types.Operator) => {
    onSetOuterOperator(newOperator);
    setOuterOperatorAnchorEl(null);
    onUpdateFilteredUsers && onUpdateFilteredUsers(filterGroups, newOperator);
  };

  const [dropdownContentType, setDropdownContentType] = useState<Types.DropdownContentType>(
    CONTENT_TYPE_FIELD,
  );

  const handleOpenDropdown = (e: React.MouseEvent<HTMLElement>) => {
    setDropdownAnchorEl(e.currentTarget);
  };

  const handleDeleteFilter = (groupId: string, filterId: string) => {
    const newFilterGroups = filterGroups
      .map((group: Types.FilterGroup) =>
        group.id === groupId
          ? { ...group, filters: group.filters.filter(filter => filter.id !== filterId) }
          : group,
      )
      .filter(group => group.filters.length > 0);

    onSetFilterGroups(newFilterGroups);
    setDropdownAnchorEl(null);
    onUpdateFilteredUsers && onUpdateFilteredUsers(newFilterGroups);
  };

  const handleAddFilter = (groupId: string) => {
    const tempId = Date.now().toString();
    onSetFilterGroups(
      filterGroups.map((group: Types.FilterGroup) =>
        group.id === groupId
          ? {
              ...group,
              filters: [...group.filters, { id: tempId, field: '', comparison: '', value: '' }],
            }
          : group,
      ),
    );

    setFilterIdInFocus(tempId);
    setDropdownContentType(CONTENT_TYPE_FIELD);
  };

  const handleAddFilterGroup = () => {
    analytics.track('Roles/Permissions - Create Role - Add Filter Button Clicked', {
      category: 'AD - Roles/Permissions',
    });

    const tempFilterId = Date.now().toString();

    onSetFilterGroups([
      ...filterGroups,
      {
        id: Date.now().toString(),
        filters: [{ id: tempFilterId, field: '', comparison: '', value: '' }],
        operator: 'and' as Types.Operator,
      },
    ]);

    setFilterIdInFocus(tempFilterId);
    setDropdownContentType(CONTENT_TYPE_FIELD);
  };

  const handleEditFilterField = (filterId: string, newField: Types.FilterField) => {
    const newFilterProps = RELATIVE_FILTERS.map(filter => filter.type).includes(newField.type)
      ? { comparison: 'relative', type: newField.type }
      : { field: newField, comparison: '', value: '' };

    onSetFilterGroups(
      filterGroups.map((group: Types.FilterGroup) => ({
        ...group,
        filters: group.filters.map(filter =>
          filter.id === filterId ? { ..._omit(filter, 'type'), ...newFilterProps } : filter,
        ),
      })),
    );
  };

  const handleEditSecondFilterField = (filterId: string, newField: Types.FilterField) => {
    onSetFilterGroups(
      filterGroups.map((group: Types.FilterGroup) => ({
        ...group,
        filters: group.filters.map(filter =>
          filter.id === filterId
            ? { ...filter, comparison: 'relative', type: 'same-attribute', field: newField }
            : filter,
        ),
      })),
    );
  };

  const handleEditFilterComparison = (filterId: string, newComparison: string) => {
    const newFilterGroups = filterGroups.map((group: Types.FilterGroup) => ({
      ...group,
      filters: group.filters.map(filter =>
        filter.id === filterId ? { ...filter, comparison: newComparison, value: '' } : filter,
      ),
    }));

    onSetFilterGroups(newFilterGroups);

    if (['hav', 'ukn'].includes(newComparison) && onUpdateFilteredUsers) {
      onUpdateFilteredUsers(newFilterGroups);
    }
  };

  const handleEditFilterValue = (filterId: string, newValue: string) => {
    const newFilterGroups = filterGroups.map((group: Types.FilterGroup) => ({
      ...group,
      filters: group.filters.map(filter =>
        filter.id === filterId ? { ...filter, value: newValue } : filter,
      ),
    }));

    onSetFilterGroups(newFilterGroups);
    onUpdateFilteredUsers && onUpdateFilteredUsers(newFilterGroups);
  };

  const handleSetDropdownAnchor = (element: HTMLElement | null) => {
    setDropdownAnchorEl(element);
  };

  const handleNext = async (
    currentType: Types.DropdownContentType,
    options?: {
      fieldInFocus?: Types.FilterField;
      comparisonInFocus?: string;
    },
  ) => {
    if (currentType === CONTENT_TYPE_FIELD) {
      const fieldInFocus = options && options.fieldInFocus;

      const isRelativeFilter =
        fieldInFocus && RELATIVE_FILTERS.map(f => f.type).includes(fieldInFocus.type);
      const fieldIsSameAttr = fieldInFocus && fieldInFocus.type === SAME_ATTR_RELATIVE_FILTER.type;

      if (fieldIsSameAttr) {
        setDropdownContentType(CONTENT_TYPE_FIELD_FOR_SAME_ATTR);
      } else if (isRelativeFilter) {
        await setDropdownContentType(null);
        await setDropdownAnchorEl(null);
      } else {
        setDropdownContentType(CONTENT_TYPE_COMPARISON);
      }
    } else if (currentType === CONTENT_TYPE_COMPARISON) {
      if (['hav', 'ukn'].includes(_get(options, 'comparisonInFocus', ''))) {
        await setDropdownContentType(null);
        await setDropdownAnchorEl(null);
      } else {
        setDropdownContentType(CONTENT_TYPE_VALUE);
      }
    } else {
      await setDropdownContentType(null);
      await setDropdownAnchorEl(null);
    }
  };

  const handleBack = (currentType: Types.DropdownContentType) => {
    if (currentType === CONTENT_TYPE_VALUE) {
      setDropdownContentType(CONTENT_TYPE_COMPARISON);
    } else if (currentType === CONTENT_TYPE_COMPARISON) {
      setDropdownContentType(CONTENT_TYPE_FIELD);
    } else {
      setDropdownContentType(CONTENT_TYPE_FIELD);
    }
  };

  const handleSetFilterIdInFocus = (filterId: string) => {
    setFilterIdInFocus(filterId);
  };

  const handleClearAll = () => {
    onSetFilterGroups([]);
    onSetEveryoneSelected(false);
  };

  return (
    <>
      <Paper variant="outlined" className={classes.root}>
        {filterGroups.length === 0 ? (
          <Empty
            everyoneSelected={everyoneSelected}
            onSetEveryoneSelected={onSetEveryoneSelected}
            disabled={disabled}
          />
        ) : (
          <Box display="flex" flexWrap={'wrap'} className={classes.filterGroupsContainer}>
            {filterGroups.map((filterGroup, index) => {
              return (
                <React.Fragment key={filterGroup.id}>
                  <FilterGroup
                    filterGroup={filterGroup}
                    onDeleteFilter={handleDeleteFilter}
                    onAddFilter={handleAddFilter}
                    onOpenDropdown={handleOpenDropdown}
                    onSetDropdownAnchorEl={handleSetDropdownAnchor}
                    filterIdInFocus={filterIdInFocus}
                    onSetFilterIdInFocus={handleSetFilterIdInFocus}
                    dropdownContentType={dropdownContentType}
                    onSetDropdownContentType={setDropdownContentType}
                    filterGroups={filterGroups}
                    onSetFilterGroups={onSetFilterGroups}
                    disabled={disabled}
                    onUpdateFilteredUsers={onUpdateFilteredUsers}
                  />
                  {index < filterGroups.length - 1 && (
                    <>
                      <Button
                        variant="text"
                        endIcon={<ArrowDropDownIcon className={classes.downIcon} />}
                        className={classes.operatorButton}
                        onClick={e => setOuterOperatorAnchorEl(e.currentTarget)}
                      >
                        {outerOperator}
                      </Button>
                      <Popper
                        open={!!outerOperatorAnchorEl}
                        anchorEl={outerOperatorAnchorEl}
                        transition
                        disablePortal
                        className={classes.outerOperatorPopper}
                      >
                        <Paper className={classes.outerOperatorPaper}>
                          <SelectInput
                            options={['and', 'or']}
                            onSelect={handleChangeOuterOperator}
                            className={classes.operatorSelect}
                          />
                        </Paper>
                      </Popper>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{ marginTop: 16 }}
        >
          <Button color="inherit" size="small" onClick={handleClearAll} disabled={disabled}>
            Clear All
          </Button>
          {pageType === 'members' &&
            (isLoading(combinedStatus) ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Added Users:{' '}
                {!everyoneSelected && filterGroups.length === 0 ? (
                  0
                ) : (
                  <Link
                    variant="body2"
                    className={classes.link}
                    href={makeDirectoryUrl(`/search/user?filter=${base64Filter}`)}
                  >
                    {filteredUserCount} {pluralize('User', filteredUserCount)}
                  </Link>
                )}
              </Typography>
            ))}

          <div>
            {filterGroups.length === 0 && (
              <Button
                startIcon={<AddCircleOutlineIcon className={classes.icon} />}
                size="small"
                onClick={() => onSetEveryoneSelected(true)}
                disabled={shouldDisableAddFilter || disabled}
                className={classes.addEveryoneButton}
              >
                Add Everyone
              </Button>
            )}

            <Button
              startIcon={<AddCircleIcon className={classes.icon} />}
              size="small"
              onClick={handleAddFilterGroup}
              disabled={shouldDisableAddFilter || disabled}
            >
              Add Filter
            </Button>
          </div>
        </Box>
      </Paper>
      <FilterEditPopper
        anchorEl={dropdownAnchorEl}
        dropdownContentType={dropdownContentType}
        filterGroups={filterGroups}
        onNext={handleNext}
        onBack={handleBack}
        onEditFilterComparison={handleEditFilterComparison}
        onEditFilterField={handleEditFilterField}
        onEditSecondFilterField={handleEditSecondFilterField}
        onEditFilterValue={handleEditFilterValue}
        filterIdInFocus={filterIdInFocus}
      />
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  filterGroupsContainer: {
    rowGap: `${theme.spacing()}px`,
  },
  userCount: {
    fontWeight: theme.typography.fontWeight.semiBold,
  },
  operatorButton: {
    fontWeight: theme.typography.fontWeight.semiBold,
  },
  downIcon: { marginLeft: -theme.spacing() },
  outerOperatorPaper: { zIndex: theme.zIndex.modal },
  outerOperatorPopper: {
    zIndex: theme.zIndex.modal,
  },
  icon: {
    width: 20,
    height: 20,
  },
  addEveryoneButton: {
    marginRight: theme.spacing(),
  },
  operatorSelect: {
    textTransform: 'capitalize',
  },
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    fontWeight: theme.typography.fontWeight.semiBold,
  },
}));

export default Filters;
